import { ActionReducer, createReducer, on } from '@ngrx/store';
import {
  addComment_Success,
  addTaskFilter,
  changeTaskStatus_Success,
  fetchTaskDetails,
  fetchTaskDetails_Success,
  fetchTasks,
  fetchTasks_Failure,
  fetchTasks_Success,
  fetchTaskStatuses,
  fetchTaskStatuses_Failure,
  fetchTaskStatuses_Success,
  markTaskAsReviewed,
  markTasksAsReviewed_Success,
  removeAllFilters,
  removeTaskDetailsSidebar,
  removeTaskFilter,
  resetTasksStore,
  setTaskDetailsCommentFailed,
  setTaskDetailsCommentPending,
  toggleSidebar,
} from './tasks.actions';
import {
  Task,
  ImplementationTimelineEnum,
  SeverityEnum,
  TaskDetails,
  CHART_COLORS,
  StatusDto,
  TaskStatusEnum,
} from '../../../core/interfaces';

export const tasksFeatureKey = 'tasks';

export interface Filter {
  fieldName: keyof TaskFilter;
  displayName: string;
  value: string | number;
}

export interface TaskFilter {
  severity: SeverityEnum[]; // Can select multiple severities
  implementationTimeline: ImplementationTimelineEnum[];
  ownership: string[];
  domains: string[]; // Domain IDs
  assignedRoles: string[];
  controlId: string[];
  statuses: TaskStatusEnum[];
}

export type TasksState = {
  filters?: Filter[];
  taskFilters: TaskFilter;
  tasks?: Task[];
  isLoading?: boolean;
  showSidebar?: boolean;
  taskDetails?: TaskDetails;
  departmentsColors?: {
    [key: string]: string;
  };
  detailsLoading?: boolean;
  selectedTaskId?: string;
  taskStatuses?: StatusDto[];
  taskStatusesLoading?: boolean;
};

export const initialState: TasksState = {
  taskFilters: {
    severity: [] as SeverityEnum[],
    implementationTimeline: [] as ImplementationTimelineEnum[],
    ownership: [] as string[],
    domains: [] as string[],
    assignedRoles: [] as string[],
    controlId: [],
    statuses: [] as TaskStatusEnum[],
  },
};

export const tasksReducer: ActionReducer<TasksState> = createReducer(
  initialState,
  on(addTaskFilter, (state, { filterType, value, displayName }) => {
    const filters: Filter[] = [
      ...(state.filters || []),
      { displayName, fieldName: filterType, value },
    ];
    return {
      ...state,
      filters,
      taskFilters: {
        ...state.taskFilters,
        [filterType]: [...state.taskFilters[filterType], value],
      },
    };
  }),
  on(removeTaskFilter, (state, { filterType, value }) => {
    const filters = state.filters?.filter(
      (item) => !(item.fieldName === filterType && item.value === value),
    );
    return {
      ...state,
      filters,
      taskFilters: {
        ...state.taskFilters,
        [filterType]: state.taskFilters[filterType].filter(
          (item) => item !== value,
        ),
      },
    };
  }),
  on(removeAllFilters, (state) => {
    return {
      ...state,
      filters: undefined,
      taskFilters: initialState.taskFilters,
    };
  }),
  on(fetchTasks_Success, (state: TasksState, { tasks }): TasksState => {
    const departmentsColors = { ...state.departmentsColors };
    // Process all departments from all tasks
    tasks.forEach((task) => {
      if (task.raci && task.raci.departments) {
        // Extract department IDs from the raci.departments object
        Object.entries(task.raci.departments).forEach(([_, departments]) => {
          if (Array.isArray(departments)) {
            departments.forEach((dept) => {
              if (dept && dept.id && !departmentsColors[dept.id]) {
                const colorIndex =
                  Object.keys(departmentsColors).length % CHART_COLORS.length;
                departmentsColors[dept.id] = CHART_COLORS[colorIndex];
              }
            });
          } else if (
            departments &&
            departments.id &&
            !departmentsColors[departments.id]
          ) {
            const colorIndex =
              Object.keys(departmentsColors).length % CHART_COLORS.length;
            departmentsColors[departments.id] = CHART_COLORS[colorIndex];
          }
        });
      }
    });

    return {
      ...state,
      tasks,
      departmentsColors,
      isLoading: false,
    };
  }),
  on(
    fetchTasks_Failure,
    (state: TasksState): TasksState => ({
      ...state,
      isLoading: false,
    }),
  ),
  on(changeTaskStatus_Success, (state: TasksState, { task }): TasksState => {
    return {
      ...state,
      tasks: state.tasks?.map((defaultTask) =>
        defaultTask.id === task.id ? { ...task } : defaultTask,
      ),
    };
  }),
  on(markTaskAsReviewed, (state: TasksState, { taskId }): TasksState => {
    return {
      ...state,
      tasks: state.tasks?.map((defaultTask) =>
        defaultTask.id === taskId
          ? { ...defaultTask, isReviewed: true }
          : defaultTask,
      ),
    };
  }),
  on(fetchTasks, (state: TasksState, { bypassLoader }): TasksState => {
    return {
      ...state,
      isLoading: !bypassLoader,
    };
  }),
  on(markTasksAsReviewed_Success, (state) => {
    const newTasks: Task[] =
      state.tasks?.filter((task) => !task.isReviewed) || [];
    const reviewedTasks: Task[] =
      state.tasks?.filter((task) => task.isReviewed) || [];
    return {
      ...state,
      tasks: [
        ...newTasks?.map((task) => ({
          ...task,
          isReviewed: true,
        })),
        ...reviewedTasks,
      ] as Task[],
    };
  }),
  on(fetchTaskDetails_Success, (state, { taskDetails }) => {
    return {
      ...state,
      taskDetails,
      detailsLoading: false,
    };
  }),
  on(fetchTaskDetails, (state, { taskId, bypassLoader }) => {
    return {
      ...state,
      selectedTaskId: taskId,
      detailsLoading: !bypassLoader,
    };
  }),
  on(removeTaskDetailsSidebar, (state) => {
    return {
      ...state,
      taskDetails: undefined,
      selectedTaskId: undefined,
    };
  }),
  on(addComment_Success, (state, { comment }) => {
    const comments = [...(state.taskDetails?.comments || [])];
    comments.unshift(comment);
    return {
      ...state,
      taskDetails: {
        ...state.taskDetails,
        comments,
        newComment: comment,
      } as TaskDetails,
    };
  }),
  on(setTaskDetailsCommentPending, (state, { pending }) => {
    return {
      ...state,
      taskDetails: {
        ...state.taskDetails,
        taskDetailsCommentPending: pending,
      } as TaskDetails,
    };
  }),
  on(setTaskDetailsCommentFailed, (state, { failed, errorMessage }) => {
    return {
      ...state,
      taskDetails: {
        ...state.taskDetails,
        taskDetailsCommentFailed: failed,
        commentErrorMessage: failed ? errorMessage : undefined,
      } as TaskDetails,
    };
  }),
  on(
    toggleSidebar,
    (state, { showSidebar }): TasksState => ({
      ...state,
      showSidebar,
    }),
  ),
  on(resetTasksStore, (): TasksState => initialState),
  on(fetchTaskStatuses, (state, { bypassLoader }): TasksState => {
    return {
      ...state,
      taskStatusesLoading: !bypassLoader,
    };
  }),
  on(fetchTaskStatuses_Success, (state, { taskStatuses }) => {
    return {
      ...state,
      taskStatusesLoading: false,
      taskStatuses,
    };
  }),
  on(fetchTaskStatuses_Failure, (state) => {
    return {
      ...state,
      taskStatusesLoading: false,
    };
  }),
);
